import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'

import {
  assignFieldTill,
  closeTellerSession,
  createTill,
  createVault,
  moveCash,
  openTellerSession,
  postLoanRepayment,
  postSavingsDeposit,
  postSavingsWithdrawal,
  recordCashCount,
  tellerKeys,
  topUpSecurityDeposit,
} from '@/api/teller-api'
import type {
  AssignFieldTillRequest,
  CashMovementRequest,
  CloseTellerSessionRequest,
  CounterRepaymentRequest,
  CounterSavingsMovementRequest,
  CreateTillRequest,
  CreateVaultRequest,
  OpenTellerSessionRequest,
  RecordCashCountRequest,
} from '@/api/teller-types'

/**
 * The commands the teller area issues.
 *
 * Every one of these changes a cash position somewhere, and cash positions are read by three
 * screens at once — the counter, the branch's cash position, a drawer's own page. So the
 * invalidation is deliberately broad: after any movement the whole `teller` key is dropped rather
 * than a hand-picked subset. A stale till balance is not a cosmetic problem; it is the number
 * somebody decides whether to bank against.
 *
 * Nothing here reports success on its own. Five of these answer with a `DualControlResult`, and it
 * is the calling dialog that reads `awaitingApproval` and says which of the two things happened.
 */

/** Everything in this module reads a cash position, so everything in it is refetched. */
function invalidateTeller(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: tellerKeys.all })
}

export function useOpenTellerSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: OpenTellerSessionRequest) => openTellerSession(request),
    onSuccess: (opened) => {
      // The response is the whole session, so the counter renders the new shift without asking for
      // what it has just been given.
      queryClient.setQueryData(tellerKeys.session(opened.id), opened)
      invalidateTeller(queryClient)
    },
  })
}

export function useCloseTellerSession(sessionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: CloseTellerSessionRequest) => closeTellerSession(sessionId, request),
    onSuccess: () => {
      // Refetched on both paths. On the deferred one the session has still changed — it is in
      // `PendingClose` and has stopped trading — which is exactly the state the screen must show.
      invalidateTeller(queryClient)
    },
  })
}

export function useRecordCashCount(sessionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: RecordCashCountRequest) => recordCashCount(sessionId, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tellerKeys.session(sessionId) })
      void queryClient.invalidateQueries({ queryKey: tellerKeys.sessions() })
    },
  })
}

export function useMoveCash() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: CashMovementRequest) => moveCash(request),
    onSuccess: () => {
      invalidateTeller(queryClient)
    },
  })
}

export function useCreateTill() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: CreateTillRequest) => createTill(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tellerKeys.tills() })
    },
  })
}

export function useCreateVault() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: CreateVaultRequest) => createVault(request),
    onSuccess: () => {
      invalidateTeller(queryClient)
    },
  })
}

export function useAssignFieldTill(tillId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: AssignFieldTillRequest) => assignFieldTill(tillId, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tellerKeys.tills() })
    },
  })
}

/**
 * The counter's cash-in and cash-out rails.
 *
 * Each invalidates the account it posted to and the teller keys behind it: a deposit raises the
 * drawer's cash-in and therefore the expected closing figure the same teller will be counted
 * against at the end of the shift.
 */
function invalidateAccount(queryClient: QueryClient, accountId: string) {
  void queryClient.invalidateQueries({ queryKey: tellerKeys.savingsAccount(accountId) })
  void queryClient.invalidateQueries({ queryKey: tellerKeys.counter() })
  invalidateTeller(queryClient)
}

export function useCounterDeposit(accountId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: CounterSavingsMovementRequest) => postSavingsDeposit(accountId, request),
    onSuccess: () => {
      invalidateAccount(queryClient, accountId)
    },
  })
}

export function useCounterSecurityDeposit(accountId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: CounterSavingsMovementRequest) => topUpSecurityDeposit(accountId, request),
    onSuccess: () => {
      invalidateAccount(queryClient, accountId)
    },
  })
}

export function useCounterWithdrawal(accountId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: CounterSavingsMovementRequest) => postSavingsWithdrawal(accountId, request),
    onSuccess: () => {
      // Refetched on both paths: on the deferred one nothing has moved, and the refetch is what
      // proves it — the balance on screen after a queued withdrawal is the balance before it.
      invalidateAccount(queryClient, accountId)
    },
  })
}

export function useCounterRepayment(cycleId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: CounterRepaymentRequest) => postLoanRepayment(cycleId, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tellerKeys.counter() })
      invalidateTeller(queryClient)
    },
  })
}
